import {
  Building2,
  Search,
  Library,
  LogOut,
  User,
  Crown,
  ChevronRight,
  Moon,
  SunMedium,
  Sparkles,
} from "lucide-react";
import easlyLogoWhite from "@/assets/logo/easly-logo_white.svg";
import easlyLogoBlack from "@/assets/logo/easly-logo_black.svg";
import easlyIconWhite from "@/assets/logo/easly-icon_white.svg";
import { NavLink } from "@/components/NavLink";
import { Link, useNavigate } from "react-router-dom";
import { useTheme } from "next-themes";
import { useAuth } from "@/context/AuthContext";
import { CreditsBadge } from "@/components/subscription/CreditsBadge";
import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarHeader,
  SidebarFooter,
  useSidebar,
} from "@/components/ui/sidebar";
import { useTranslation } from "react-i18next";
import { track } from "@/lib/analytics";
import { UserSettingsSheet } from "./UserSettingsSheet";

export function AppSidebar() {
  const { t } = useTranslation();
  const { state } = useSidebar();
  const collapsed = state === "collapsed";
  const { theme, resolvedTheme, setTheme } = useTheme();
  const isDark = theme === "dark" || (theme === "system" && resolvedTheme === "dark");
  const navigate = useNavigate();
  const { signOut, user } = useAuth();

  const managerNavItems = [
    { title: t("appSidebar.companyProfile"), url: "/company-profile", icon: Building2 },
    { title: t("appSidebar.recommendations"), url: "/recommendations", icon: Sparkles },
    { title: t("appSidebar.salesOutreach"), url: "/sales-outreach", icon: Search },
    { title: t("appSidebar.analyzedCompanies"), url: "/analyzed-companies", icon: Library },
  ];

  const clientNavItems = [{ title: t("appSidebar.myProfile"), url: "/client-profile", icon: User }];

  const navItems = user?.role === "Client" ? clientNavItems : managerNavItems;

  const handleSignOut = () => {
    track("sign_out", { role: user?.role });
    signOut();
    navigate("/login");
  };

  const initials = user?.fullName
    ? user.fullName
        .split(" ")
        .map((n) => n[0])
        .slice(0, 2)
        .join("")
        .toUpperCase()
    : "U";

  const footerButtonClass = `flex items-center gap-2.5 w-full py-2 rounded-lg text-muted-foreground hover:text-foreground hover:bg-sidebar-accent transition-all duration-150 text-sm ${collapsed ? "justify-center px-2" : "px-3"}`;

  return (
    <>
      <Sidebar collapsible="icon" className="border-r border-sidebar-border">
        {/* Logo */}
        <SidebarHeader className="px-4 py-5">
          <Link to="/" className="flex items-center group">
            {collapsed ? (
              <img
                src={easlyIconWhite}
                alt="Easly"
                className="h-7 w-auto"
                style={!isDark ? { filter: "brightness(0)" } : undefined}
              />
            ) : (
              <img
                src={isDark ? easlyLogoWhite : easlyLogoBlack}
                alt="Easly"
                className="h-7 w-auto"
              />
            )}
          </Link>
        </SidebarHeader>

        {/* Nav */}
        <SidebarContent className="px-2">
          {!collapsed && (
            <p className="px-3 pt-1 pb-2 text-[10px] font-semibold uppercase tracking-widest text-muted-foreground/60">
              {t("appSidebar.navigation")}
            </p>
          )}
          <SidebarGroup>
            <SidebarGroupContent>
              <SidebarMenu className="space-y-0.5">
                {navItems.map((item) => (
                  <SidebarMenuItem key={item.title}>
                    <SidebarMenuButton asChild tooltip={item.title}>
                      <NavLink
                        to={item.url}
                        onClick={() => track("sidebar_nav_clicked", { url: item.url })}
                        className={`group/nav relative flex items-center gap-2.5 py-2.5 rounded-lg text-sm font-medium text-sidebar-foreground/65 hover:bg-sidebar-accent hover:text-sidebar-foreground transition-all duration-150 ${collapsed ? "justify-center px-2" : "px-3"}`}
                        activeClassName="bg-primary/[0.07] text-primary font-semibold hover:bg-primary/[0.1] hover:text-primary nav-item-active"
                      >
                        <item.icon className="w-4 h-4 flex-shrink-0 transition-transform duration-150 group-hover/nav:scale-110" />
                        {!collapsed && <span>{item.title}</span>}
                      </NavLink>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                ))}
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        </SidebarContent>

        {/* Footer */}
        <SidebarFooter className="px-3 py-4 space-y-1">
          {!collapsed && user?.role !== "Client" && (
            <Link
              to="/subscription"
              className="group flex items-center justify-between px-3 py-2.5 rounded-lg border transition-all duration-200 hover:scale-[1.01] mb-1"
              style={{
                background: "hsl(var(--easly-gold-light))",
                borderColor: "hsl(var(--easly-gold) / 0.25)",
              }}
            >
              <div className="flex items-center gap-2">
                <Crown
                  className={`w-3.5 h-3.5 ${user?.hasPremium ? "text-easly-gold" : "text-muted-foreground"}`}
                />
                <span className="text-xs font-semibold text-foreground">
                  {user?.hasPremium ? t("appSidebar.premium") : t("appSidebar.freePlan")}
                </span>
              </div>
              <div className="flex items-center gap-1.5">
                <CreditsBadge />
                <ChevronRight className="w-3 h-3 text-muted-foreground group-hover:translate-x-0.5 transition-transform" />
              </div>
            </Link>
          )}

          {user?.fullName && (
            <UserSettingsSheet>
              <button
                className={`flex items-center gap-2.5 py-1.5 rounded-lg mb-1 w-full hover:bg-sidebar-accent transition-colors duration-150 group/user ${collapsed ? "justify-center px-2" : "px-2"}`}
              >
                <div className="w-7 h-7 rounded-full bg-gradient-to-br from-easly-blue/80 to-primary flex items-center justify-center text-[11px] font-bold text-white flex-shrink-0 shadow-sm">
                  {initials}
                </div>
                {!collapsed && (
                  <div className="min-w-0 flex-1 text-left">
                    <p className="text-xs font-semibold text-foreground truncate">
                      {user.fullName}
                    </p>
                    {user.email && (
                      <p className="text-[10px] text-muted-foreground truncate">{user.email}</p>
                    )}
                  </div>
                )}
                {!collapsed && (
                  <ChevronRight className="w-3 h-3 text-muted-foreground/50 flex-shrink-0 group-hover/user:translate-x-0.5 transition-transform duration-150" />
                )}
              </button>
            </UserSettingsSheet>
          )}

          <div className="border-t border-sidebar-border pt-1">
            <button
              onClick={() => {
                const next = isDark ? "light" : "dark";
                track("theme_toggled", { theme: next });
                setTheme(next);
              }}
              title={isDark ? t("topBar.lightMode") : t("topBar.darkMode")}
              className={footerButtonClass}
            >
              {isDark ? (
                <SunMedium className="w-4 h-4 flex-shrink-0 text-easly-gold" />
              ) : (
                <Moon className="w-4 h-4 flex-shrink-0" />
              )}
              {!collapsed && <span>{isDark ? t("topBar.lightMode") : t("topBar.darkMode")}</span>}
            </button>

            <button
              onClick={handleSignOut}
              title={t("appSidebar.logOut")}
              className={`${footerButtonClass} group/signout`}
            >
              <LogOut className="w-4 h-4 flex-shrink-0 group-hover/signout:-translate-x-0.5 transition-transform duration-150" />
              {!collapsed && <span>{t("appSidebar.logOut")}</span>}
            </button>
          </div>
        </SidebarFooter>
      </Sidebar>
    </>
  );
}
